import React, { useState, useMemo } from 'react';
import { useCareFlow360Store } from '../../careflow360/hooks/useCareFlow360Store';

export interface ClinicNote {
  id: string;
  columnId: string;
  title: string;
  content: string;
  author: string;
  date: string;
  tags?: string[];
  isArchived?: boolean;
}

export interface KanbanColumnDef {
  id: string;
  title: string;
  themeColor: string;
}

const DEFAULT_COLUMNS: KanbanColumnDef[] = [
  { id: 'todo', title: 'To Do', themeColor: '#e2e8f0' },
  { id: 'doing', title: 'In Progress', themeColor: '#bae6fd' },
  { id: 'done', title: 'Done', themeColor: '#bbf7d0' },
  { id: 'general', title: 'General Info', themeColor: '#fef08a' }
];

export interface ClinicNotesBoardProps {
  visitId?: number | null;
  patientId?: string;
}

export default function ClinicNotesBoard({ visitId, patientId }: ClinicNotesBoardProps) {
  const { events, updateEvent } = useCareFlow360Store();
  const [columns, setColumns] = useState<KanbanColumnDef[]>(DEFAULT_COLUMNS);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAsc, setSortAsc] = useState(false);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const currentUser = "Doctor"; 

  const notes = useMemo(() => {
    return events.map(ev => ({
      id: ev.id,
      columnId: ev.columnId || 'todo',
      title: `Tooth ${ev.toothId}: ${ev.label}`,
      content: ev.note || '',
      author: 'Doctor', 
      date: new Date(ev.timestamp).toLocaleDateString(),
      isArchived: false,
      tags: ev.type === 'treatment' ? ['Treatment'] : ['Condition']
    })) as ClinicNote[];
  }, [events]);

  const filteredNotes = useMemo(() => {
    let result = notes.filter(n => !n.isArchived);
    
    if (showOnlyMine) {
      result = result.filter(n => n.author === currentUser);
    }
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(n => 
        n.title.toLowerCase().includes(q) || 
        n.content.toLowerCase().includes(q)
      );
    }
    
    result.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortAsc ? dateA - dateB : dateB - dateA;
    });
    
    return result;
  }, [notes, searchQuery, sortAsc, showOnlyMine]);

  const moveNote = (noteId: string, newColumnId: string) => {
    const event = events.find(e => e.id === noteId);
    if (event) {
      updateEvent(noteId, { ...event, columnId: newColumnId });
    }
  };

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('noteId', id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    const noteId = e.dataTransfer.getData('noteId');
    if (noteId) {
      moveNote(noteId, columnId);
    }
  };

  return (
    <div className="apexo-kanban-wrapper">
      <div className="apexo-kanban-toolbar">
        <div className="apexo-kanban-search">
          <span className="search-icon">🔍</span>
          <input 
            type="text" 
            placeholder="Search notes placeholder..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="apexo-kanban-actions">
          <button 
            className="apexo-kb-btn"
            onClick={() => setSortAsc(!sortAsc)}
          >
            {sortAsc ? '🔼 Sort Ascending' : '🔽 Sort Descending'}
          </button>
          
          <button 
            className={`apexo-kb-btn ${showOnlyMine ? 'active' : ''}`}
            onClick={() => setShowOnlyMine(!showOnlyMine)}
          >
            {showOnlyMine ? '👥 Show All' : '👤 Show Only Mine'}
          </button>
        </div>
      </div>

      <div className="apexo-kanban-board">
        {columns.map(col => {
          const colNotes = filteredNotes.filter(n => n.columnId === col.id);
          
          return (
            <div 
              key={col.id} 
              className="apexo-kb-column"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.id)}
            >
              <div 
                className="apexo-kb-column-header"
                style={{ borderTopColor: col.themeColor }}
              >
                <h3 className="apexo-kb-col-title">{col.title}</h3>
                <span className="apexo-kb-badge">{colNotes.length}</span>
              </div>
              
              <div className="apexo-kb-column-body">
                {colNotes.map(n => (
                  <div 
                    key={n.id} 
                    className="apexo-kb-card"
                    draggable
                    onDragStart={(e) => handleDragStart(e, n.id)}
                  >
                    <div className="apexo-kb-card-header">
                      <h4 className="apexo-kb-card-title">{n.title}</h4>
                      <button className="apexo-kb-card-menu">⋯</button>
                    </div>
                    <p className="apexo-kb-card-content">{n.content}</p>
                    
                    {n.tags && n.tags.length > 0 && (
                      <div className="apexo-kb-card-tags">
                        {n.tags.map(t => (
                          <span key={t} className="apexo-kb-tag">{t}</span>
                        ))}
                      </div>
                    )}
                    
                    <div className="apexo-kb-card-footer">
                      <span className="apexo-kb-card-author">👤 {n.author}</span>
                      <span className="apexo-kb-card-date">{n.date}</span>
                    </div>
                  </div>
                ))}
                {colNotes.length === 0 && (
                  <div className="apexo-kb-empty-zone">Drop notes here</div>
                )}
              </div>
            </div>
          );
        })}
        
        <div className="apexo-kb-add-column">
          <button className="apexo-kb-add-btn">
            <span className="icon">＋</span>
            <span className="text">Add Column</span>
          </button>
        </div>
      </div>
    </div>
  );
}
